import { useState, type FormEvent } from 'react';
import { Loader2, Pencil, Plus, Shield, ShieldCheck, Trash2, X } from 'lucide-react';
import {
  PERMISSION_CATALOG,
  hasPermission,
  type Permission,
  type PermissionMeta,
  type RoleProfile,
} from '@datazo/contracts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ApiClientError } from '@/shared/api/client';
import { useAuthStore } from '@/shared/store/auth';
import { usePageTitle } from '@/shared/store/page';
import { useToast } from '@/shared/toast/toast-context';
import { useRoles, useCreateRole, useUpdateRole, useDeleteRole } from './api';

/** Grupos del catálogo de permisos, en orden de aparición. */
const GROUPS = PERMISSION_CATALOG.reduce<{ grupo: string; perms: PermissionMeta[] }[]>(
  (acc, meta) => {
    let g = acc.find((x) => x.grupo === meta.grupo);
    if (!g) {
      g = { grupo: meta.grupo, perms: [] };
      acc.push(g);
    }
    g.perms.push(meta);
    return acc;
  },
  [],
);

export function RolesPage(): JSX.Element {
  usePageTitle('Perfiles y permisos', 'Sistema · Roles a medida');
  const permisos = useAuthStore((s) => s.user?.permisos ?? []);
  const canManage = hasPermission(permisos, 'roles:manage');
  const rolesQuery = useRoles();
  const [editing, setEditing] = useState<RoleProfile | null>(null);
  const [creating, setCreating] = useState(false);

  const roles = rolesQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-muted">
          Los perfiles del sistema no se pueden editar. Crea perfiles a medida marcando
          exactamente qué accesos y funciones tendrá cada uno.
        </p>
        {canManage && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="w-4 h-4" /> Nuevo perfil
          </Button>
        )}
      </div>

      {rolesQuery.isLoading ? (
        <div className="grid place-items-center py-16 text-text-muted">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {roles.map((r) => (
            <Card key={r.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className="inline-grid place-items-center w-10 h-10 rounded-md bg-primary/10 text-primary flex-shrink-0">
                    {r.esSistema ? <Shield className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-text">{r.nombre}</span>
                      <Badge variant={r.esSistema ? 'gray' : 'green'}>
                        {r.esSistema ? 'Sistema' : 'Personalizado'}
                      </Badge>
                    </div>
                    {r.descripcion !== undefined && (
                      <p className="text-xs text-text-muted mt-0.5">{r.descripcion}</p>
                    )}
                    <p className="text-xs text-text-muted mt-1">
                      {r.permisos.includes('*')
                        ? 'Acceso total'
                        : `${r.permisos.length} permiso(s)`}
                    </p>
                  </div>
                </div>
                {canManage && !r.esSistema && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="secondary" size="sm" onClick={() => setEditing(r)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <DeleteRoleButton role={r} />
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <RoleModal
          role={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function DeleteRoleButton({ role }: { role: RoleProfile }): JSX.Element {
  const del = useDeleteRole();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const onDelete = async (): Promise<void> => {
    try {
      await del.mutateAsync(role.id);
      showToast({ kind: 'success', title: 'Perfil eliminado' });
      setOpen(false);
    } catch (err) {
      showToast({
        kind: 'danger',
        title: 'No se pudo eliminar',
        description: err instanceof ApiClientError ? err.message : 'Error inesperado',
      });
    }
  };
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)} disabled={del.isPending}>
        <Trash2 className="w-3.5 h-3.5 text-danger" />
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        danger
        loading={del.isPending}
        title="Eliminar perfil"
        description={`Vas a eliminar el perfil "${role.nombre}". Los usuarios con este perfil deben reasignarse antes.`}
        confirmLabel="Eliminar"
        onConfirm={() => void onDelete()}
      />
    </>
  );
}

function RoleModal({
  role,
  onClose,
}: {
  role: RoleProfile | null;
  onClose: () => void;
}): JSX.Element {
  const isEdit = role !== null;
  const create = useCreateRole();
  const update = useUpdateRole();
  const { showToast } = useToast();
  const [nombre, setNombre] = useState(role?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(role?.descripcion ?? '');
  const [selected, setSelected] = useState<Set<Permission>>(
    new Set((role?.permisos ?? []).filter((p): p is Permission => p !== '*')),
  );
  const [error, setError] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;

  const toggle = (key: Permission): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleGroup = (perms: readonly Permission[], on: boolean): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of perms) {
        if (on) next.add(p);
        else next.delete(p);
      }
      return next;
    });

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const permisos = [...selected];
    if (permisos.length === 0) {
      setError('Selecciona al menos un permiso.');
      return;
    }
    try {
      if (isEdit && role) {
        await update.mutateAsync({ id: role.id, input: { nombre, descripcion, permisos } });
      } else {
        await create.mutateAsync({ nombre, descripcion, permisos });
      }
      showToast({ kind: 'success', title: isEdit ? 'Perfil actualizado' : 'Perfil creado' });
      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'No se pudo guardar el perfil.');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
    >
      <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <h2 className="text-lg font-semibold">
              {isEdit ? `Editar perfil: ${role?.nombre}` : 'Nuevo perfil'}
            </h2>
            <button type="button" onClick={onClose} className="text-text-muted hover:text-text">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="rol-nombre">Nombre del perfil</Label>
                <Input
                  id="rol-nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ej: Recepción Plus"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rol-desc">Descripción (opcional)</Label>
                <Input
                  id="rol-desc"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Para qué sirve este perfil"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Accesos y funciones</Label>
              {GROUPS.map((g) => {
                const keys = g.perms.map((p) => p.key);
                const allOn = keys.every((k) => selected.has(k));
                return (
                  <div key={g.grupo} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text">{g.grupo}</span>
                      <button
                        type="button"
                        onClick={() => toggleGroup(keys, !allOn)}
                        className="text-xs text-primary hover:underline"
                      >
                        {allOn ? 'Quitar todo' : 'Todo'}
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-1.5">
                      {g.perms.map((p) => (
                        <label
                          key={p.key}
                          className="flex items-center gap-2 text-sm text-text-soft cursor-pointer select-none"
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(p.key)}
                            onChange={() => toggle(p.key)}
                            className="rounded border-border text-primary focus:ring-primary/20"
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {error !== null && (
              <div className="px-3 py-2 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200">
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Guardar cambios' : 'Crear perfil'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
